import React from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { cn } from '../../lib/utils';
import {
  LayoutDashboard,
  FileText,
  Users,
  Settings,
  FolderOpen,
  UserCircle,
  Tablet,
  BarChart3,
} from 'lucide-react';

// Expanding isActive function to check if the current path matches the given path
// or starts with the given path
// This is useful for highlighting the active sidebar item
// when the user is on a subpage of that item
// import { checkIsActive } from './isActive';

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  href: string;
  active?: boolean;
  expanded?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  icon,
  label,
  href,
  active,
  expanded,
}) => {
  return (
    <Link
      to={href}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all',
        active
          ? 'bg-sidebar-primary text-sidebar-primary-foreground'
          : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        !expanded && 'justify-center py-2 px-3'
      )}
    >
      {icon}
      {expanded && <span>{label}</span>}
    </Link>
  );
};

interface SidebarProps {
  expanded?: boolean;
  onToggle?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  expanded = true,
  onToggle,
}) => {
  const currentPath = useLocation({ select: (location) => location.pathname });

  return (
    <aside
      className={cn(
        'flex h-screen flex-col border-r border-sidebar-border bg-sidebar',
        expanded ? 'w-64' : 'w-16'
      )}
    >
      <div className={cn(
          'flex h-16 items-center border-b border-sidebar-border px-4',
          expanded ? 'px-4' : 'px-2'
        )}
      >
        {expanded ? (
          <>
            <h1 className="sr-only text-xl font-semibold text-sidebar-foreground">OBTA Admin</h1>
            <img className="w-full p-4" src="/obtc-logo-full.svg" alt="OBTA Admin Logo" />
          </>
        ) : (
          <>
            <h1 className="sr-only text-xl font-semibold text-sidebar-foreground">OA</h1>
            <img className="w-full" src="/obtc-logo-icon.svg" alt="OBTA Icon Logo" />
          </>
        )}
      </div>
      <nav className="flex-1 overflow-y-auto p-2">
        <div className="space-y-1">
          <SidebarItem
            icon={<LayoutDashboard size={20} />}
            label="Dashboard"
            href="/"
            active={currentPath === '/'}
            expanded={expanded}
          />
          <SidebarItem
            icon={<FileText size={20} />}
            label="Documents"
            href="/documents"
            active={currentPath.startsWith('/documents')}
            expanded={expanded}
          />
          <SidebarItem
            icon={<FolderOpen size={20} />}
            label="Collections"
            href="/collections"
            active={currentPath.startsWith('/collections')}
            expanded={expanded}
          />
          <SidebarItem
            icon={<UserCircle size={20} />}
            label="Profiles"
            href="/profiles"
            active={currentPath.startsWith('/profiles')}
            expanded={expanded}
          />
          <SidebarItem
            icon={<Tablet size={20} />}
            label="Kiosk"
            href="/kiosk"
            active={currentPath.startsWith('/kiosk')}
            expanded={expanded}
          />
          <SidebarItem
            icon={<BarChart3 size={20} />}
            label="Analytics"
            href="/analytics"
            active={currentPath.startsWith('/analytics')}
            expanded={expanded}
          />
          <SidebarItem
            icon={<Users size={20} />}
            label="Users"
            href="/users"
            active={currentPath.startsWith('/users')}
            expanded={expanded}
          />
          <SidebarItem
            icon={<Settings size={20} />}
            label="Settings"
            href="/settings"
            active={currentPath.startsWith('/settings')}
            expanded={expanded}
          />
        </div>
      </nav>
      <div className="border-t border-sidebar-border p-2">
        <button
          onClick={onToggle}
          className="flex w-full items-center justify-center rounded-lg p-2 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          {expanded ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          ) : (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
